import { freshnessBadge, formatAsOf, formatRelativeAge, useFreshness } from "../lib/freshness";
import { updatedAtTitle } from "../lib/tabFreshness.mjs";

/**
 * MER-152: as-of timestamp + stale badge on the dashboard shell.
 * Renders inline in the sidebar so every tab signals freshness.
 *
 * The badge color shifts with age: fresh / recent are calm, stale +
 * severe stand out so Seth Heath can tell at a glance whether the
 * numbers are current or three weeks old.
 *
 * AJ 2026-09-30: `entityKey` is the entity behind the route on screen
 * (DashboardShell resolves it with entityForTabRoute), so this badge and the
 * tab's "Updated @" chip come from the same sync_log read and never disagree.
 */

export default function FreshnessBadge(props: { entityKey?: string | null }) {
  const f = useFreshness(props.entityKey);
  const badge = freshnessBadge(f.level);

  if (f.isLoading) {
    return (
      <div style={{ fontSize: 10, color: "var(--text-muted)", padding: "8px 16px" }}>
        Checking freshness...
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "10px 16px 6px",
        borderTop: "1px solid var(--border)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            padding: "3px 8px",
            borderRadius: 12,
            background: badge.bg,
            color: badge.fg,
          }}
        >
          {badge.label}
        </span>
        <span style={{ fontSize: 10, color: "var(--text-2)" }}>
          {formatRelativeAge(f.ageHours)}
        </span>
      </div>
      <div
        style={{
          fontSize: 10,
          color: "var(--text-muted)",
          lineHeight: 1.3,
        }}
        title={
          f.lastUpdated && f.entity
            ? updatedAtTitle(f.lastUpdated, f.entity, { dateOnly: f.dateOnly })
            : (f.error ?? undefined)
        }
      >
        As of {formatAsOf(f.lastUpdated, f.dateOnly)}
      </div>
    </div>
  );
}
